import { trpcClient } from '@/lib/trpc'

/**
 * 从 URL 路径中提取 canonicalSlug
 *
 * 支持格式：
 * - `/workspaces/{workspaceSlug}/docs/{slug}` → `{slug}`
 * - `/docs/{slug}` → `{slug}`
 * - `/{slug}` → `{slug}` (如 /guides/xxx, /api/xxx)
 *
 * @param path - URL 路径（不包含查询参数）
 * @returns canonicalSlug 或 null
 *
 * @example
 * extractSlugFromPath('/workspaces/my-ws/docs/guides/getting-started')
 * // => 'guides/getting-started'
 *
 * extractSlugFromPath('/guides/getting-started')
 * // => 'guides/getting-started'
 *
 * extractSlugFromPath('/docs/api/authentication')
 * // => 'api/authentication'
 */
export function extractSlugFromPath(path: string): string | null {
  if (!path || typeof path !== 'string') {
    return null
  }

  // 1. 处理 /workspaces/{workspaceSlug}/docs/{slug} 格式
  const workspaceMatch = path.match(/\/workspaces\/[^/]+\/docs\/(.+)/)
  if (workspaceMatch) {
    return workspaceMatch[1]
  }

  // 2. 处理 /docs/{slug} 格式
  if (path.startsWith('/docs/')) {
    const slug = path.slice('/docs/'.length)
    return slug || null
  }

  // 3. 处理 /{slug} 格式（section-based paths like /guides/xxx, /api/xxx）
  if (path.startsWith('/')) {
    const slug = path.slice(1)
    return slug || null
  }

  return null
}

/**
 * 补全内部文档链接的 topicId 和 docId 查询参数，并确保路径格式正确
 *
 * 处理逻辑：
 * 1. 如果链接已有完整参数且路径正确，直接返回
 * 2. 提取 canonicalSlug 并查询获取 topicId 和 docId
 * 3. 重建完整的应用内部 URL: `/workspaces/{workspaceSlug}/docs/{slug}?topicId=xxx&docId=xxx`
 *
 * @param url - 原始 URL（可能缺少参数或路径前缀）
 * @param workspaceId - 当前 workspace ID
 * @param branchId - 当前 branch ID（可选）
 * @param workspaceSlug - 当前 workspace slug（用于构建完整路径）
 * @returns 补全参数和路径后的完整 URL
 *
 * @example
 * await ensureInternalDocParams(
 *   '/guides/getting-started',
 *   'workspace-123',
 *   'branch-456',
 *   'my-workspace'
 * )
 * // => '/workspaces/my-workspace/docs/guides/getting-started?topicId=topic-789&docId=doc-abc'
 */
export async function ensureInternalDocParams(
  url: string,
  workspaceId: string,
  branchId?: string,
  workspaceSlug?: string
): Promise<string> {
  try {
    if (!url || !workspaceId) {
      return url
    }

    // 只处理内部链接
    const isInternal =
      url.startsWith('/docs/') ||
      url.startsWith('/workspaces/') ||
      url.startsWith('/guides/') ||
      url.startsWith('/api/')

    if (!isInternal) {
      return url
    }

    // 分离路径和查询字符串
    const questionMarkIndex = url.indexOf('?')
    let path = url
    let query = ''

    if (questionMarkIndex >= 0) {
      path = url.slice(0, questionMarkIndex)
      query = url.slice(questionMarkIndex + 1)
    }

    const params = new URLSearchParams(query)

    // 提取 canonicalSlug
    const slug = extractSlugFromPath(path)
    if (!slug) {
      return url
    }

    // 检查是否已经是完整的 /workspaces/{ws}/docs/{slug} 格式
    const isAlreadyFullPath = path.startsWith('/workspaces/') && path.includes('/docs/')

    // 如果已有完整参数且路径正确，直接返回
    const hasTopicId = params.has('topicId')
    const hasDocId = params.has('docId')

    if (hasTopicId && hasDocId && isAlreadyFullPath) {
      return url
    }

    // 调用 API 解析 slug → topicId + docId
    const resolved = await trpcClient.topics.resolveBySlugs.query({
      workspaceId,
      branchId,
      slugs: [slug],
    })

    const hit = resolved?.[0]
    if (hit) {
      if (!hasTopicId && hit.topicId) {
        params.set('topicId', hit.topicId)
      }
      if (!hasDocId && hit.docId) {
        params.set('docId', hit.docId)
      }
    }

    // 重建完整路径：/workspaces/{workspaceSlug}/docs/{slug}
    let finalPath = path

    // 如果路径不是完整格式，且提供了 workspaceSlug，则重建路径
    if (!isAlreadyFullPath && workspaceSlug) {
      finalPath = `/workspaces/${workspaceSlug}/docs/${slug}`
    } else if (!isAlreadyFullPath) {
      // 如果没有 workspaceSlug，尝试从 localStorage 或当前 URL 获取
      const fromPath = typeof window !== 'undefined'
        ? (window.location.pathname.match(/^\/workspaces\/([^/]+)/)?.[1] || '')
        : ''
      const currentWorkspaceSlug =
        (typeof window !== 'undefined' && localStorage.getItem('auth:activeWorkspaceSlug')) ||
        fromPath

      if (currentWorkspaceSlug) {
        finalPath = `/workspaces/${currentWorkspaceSlug}/docs/${slug}`
      }
    }

    // 重新构建 URL
    const queryString = params.toString()
    return queryString ? `${finalPath}?${queryString}` : finalPath
  } catch (error) {
    console.error('[ensureInternalDocParams] Failed to resolve link:', error)
    // 出错时返回原始 URL（降级处理）
    return url
  }
}

/**
 * 检查 URL 是否为内部文档链接
 *
 * @param url - URL 字符串
 * @returns 是否为内部链接
 *
 * @example
 * isInternalDocLink('/guides/getting-started')  // => true
 * isInternalDocLink('https://example.com')      // => false
 */
export function isInternalDocLink(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false
  }

  return (
    url.startsWith('/docs/') ||
    url.startsWith('/workspaces/') ||
    url.startsWith('/guides/') ||
    url.startsWith('/api/')
  )
}
